import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {
  CustomPropertyEditControl,
  CustomPropertyReadOnlyControl,
  CustomPropertyValuesTable,
} from '@github-ui/custom-properties-editing'
import {
  propertyFieldsToChangedPropertiesMap,
  useEditCustomProperties,
} from '@github-ui/custom-properties-editing/use-edit-custom-properties'
import type {PropertyDefinition, Repository} from '@github-ui/custom-properties-types'
import {updateOrgPropertyValuesPath} from '@github-ui/paths'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import sudo from '@github-ui/sudo'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {SearchIcon} from '@primer/octicons-react'
import {Box, Text, TextInput, useConfirm} from '@primer/react'
import {Dialog} from '@primer/react/drafts'
import {useEffect, useMemo, useState} from 'react'

import {useCurrentOrg} from '../contexts/CurrentOrgRepoContext'
import {ServerErrorFormBanner} from './Banners'

export function EditRepoPropertiesDialog({
  editingRepos,
  onDismiss,
  editableProperties,
  definitions,
  onSuccess,
}: {
  editableProperties: string[]
  editingRepos: Repository[]
  onDismiss: () => void
  definitions: PropertyDefinition[]
  onSuccess: () => void
}) {
  const customPropertiesValuesTableEnabled = useFeatureFlag('custom_properties_values_table')
  const {propertyValuesMap, setPropertyValue, revertPropertyValue} = useEditCustomProperties(
    editingRepos.map(r => r.properties || {}),
  )
  const confirmDismiss = useConfirm()

  const editableDefinitionsSet = useMemo(() => new Set(editableProperties), [editableProperties])
  const editableDefinitions = useMemo(
    () => definitions.filter(({propertyName}) => editableProperties.includes(propertyName)),
    [definitions, editableProperties],
  )

  const [submitting, setSubmitting] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const org = useCurrentOrg()

  const handleDismiss = async () => {
    const fromIsDirty = Object.values(propertyValuesMap).some(p => p.changed)
    if (!fromIsDirty) {
      return onDismiss()
    }

    const canDismiss = await confirmDismiss({
      title: 'Discard changes?',
      content: 'You have unsaved changes. Are you sure you want to discard them?',
      cancelButtonContent: 'Keep editing',
      confirmButtonContent: 'Close and discard',
      confirmButtonType: 'danger',
    })
    if (canDismiss) {
      onDismiss()
    }
  }

  const onSubmit = async () => {
    setErrorMessage('')
    if (submitting) {
      return
    }

    if (Object.values(propertyValuesMap).some(p => !!p.error)) {
      return
    }

    setSubmitting(true)

    const changedProperties = propertyFieldsToChangedPropertiesMap(definitions, propertyValuesMap)
    if (Object.keys(changedProperties).length) {
      if (!(await sudo())) {
        setSubmitting(false)
        setErrorMessage('Unauthorized')
        return
      }

      const repoIds = editingRepos.map(repo => repo.id)
      try {
        const result = await verifiedFetchJSON(updateOrgPropertyValuesPath({org: org.login}), {
          method: 'POST',
          body: {
            repoIds,
            properties: changedProperties,
          },
        })
        if (result.ok) {
          onSuccess()
        } else {
          const {error} = await result.json()
          setErrorMessage(error)
        }
      } catch {
        setErrorMessage('Failed to update properties')
      }
    } else {
      onDismiss()
    }

    setSubmitting(false)
  }

  const filteredDefinitions = definitions.filter(({propertyName}) =>
    propertyName.toLowerCase().includes(filterText.toLowerCase()),
  )

  useEffect(() => {
    const resultsFound = filteredDefinitions.length
    if (resultsFound === 1) {
      debounceAnnoucement('1 result found')
    } else {
      debounceAnnoucement(`${resultsFound} results found`)
    }
  }, [filteredDefinitions])

  if (!editingRepos.length) {
    return null
  }

  const dialogTitle = editingRepos.length === 1 ? editingRepos[0]!.name : `${editingRepos.length} repositories selected`

  if (submitting) {
    return null
  }

  const willRenderTable = customPropertiesValuesTableEnabled && filteredDefinitions.length > 0

  return (
    <Dialog
      sx={{display: 'flex', flexDirection: 'column'}}
      width="xlarge"
      onClose={handleDismiss}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <Box sx={{display: 'flex', flexDirection: 'column', background: 'none', gap: 2}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', pl: 2}}>
              <Dialog.Title id={dialogLabelId} sx={{fontWeight: 'bold'}}>
                {dialogTitle}
              </Dialog.Title>
              <Dialog.CloseButton onClose={handleDismiss} />
            </Box>
            <TextInput
              leadingVisual={SearchIcon}
              placeholder="Filter properties"
              aria-label="Filter properties"
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
            />
          </Box>
        </Dialog.Header>
      )}
      renderBody={() => (
        <Box as="form" sx={{display: 'flex', flexDirection: 'column', minHeight: 0}}>
          {errorMessage && (
            <Box sx={{m: 2}}>
              <ServerErrorFormBanner>{errorMessage}</ServerErrorFormBanner>
            </Box>
          )}
          <Dialog.Body sx={willRenderTable ? {p: 0} : undefined}>
            {filteredDefinitions.length === 0 ? (
              <Text sx={{color: 'fg.muted'}}>No properties that match</Text>
            ) : customPropertiesValuesTableEnabled ? (
              <CustomPropertyValuesTable
                definitions={filteredDefinitions}
                editableDefinitions={editableDefinitions}
                propertyValuesMap={propertyValuesMap}
                setPropertyValue={setPropertyValue}
                revertPropertyValue={revertPropertyValue}
                orgName={org.login}
              />
            ) : (
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 4}}>
                {filteredDefinitions.map(definition => {
                  const propertyName = definition.propertyName
                  const property = propertyValuesMap[propertyName]

                  if (editableDefinitionsSet.has(propertyName)) {
                    return (
                      <CustomPropertyEditControl
                        key={propertyName}
                        changed={!!property?.changed}
                        mixed={!!property?.mixed}
                        propertyValue={property?.value || ''}
                        definition={definition}
                        error={property?.error}
                        onChange={newValue => setPropertyValue(propertyName, newValue)}
                        onReset={() => revertPropertyValue(propertyName)}
                      />
                    )
                  }

                  return (
                    <CustomPropertyReadOnlyControl
                      key={propertyName}
                      propertyValue={property?.value || ''}
                      definition={definition}
                      showPermissionMessage
                    />
                  )
                })}
              </Box>
            )}
          </Dialog.Body>
        </Box>
      )}
      footerButtons={[
        {content: 'Cancel', onClick: onDismiss, sx: {mr: 1}, buttonType: 'default'},
        {content: 'Save changes', type: 'submit', onClick: onSubmit, buttonType: 'primary'},
      ]}
    />
  )
}

const debounceAnnoucement = debounce((announcement: string) => {
  announce(announcement)
}, 300)

try{ EditRepoPropertiesDialog.displayName ||= 'EditRepoPropertiesDialog' } catch {}